import { useMemo, useState } from 'react';
import type { Address, VenueValues } from '../types';
import { useOrganizationVenues } from './useOrganizationVenues';

type AddressFieldType = keyof Omit<Address, 'localizedMultiLineAddress'>;

const DISALLOWED_CHARACTERS = ['?'];
const VENUE_ADDRESS_ADDRESS_1 = 'address1';
const VENUE_ADDRESS_CITY = 'city';
const VENUE_ADDRESS_REGION = 'region';
const VENUE_ADDRESS_POSTAL = 'postalCode';
const VENUE_ADDRESS_COUNTRY = 'country';

export const ADDRESS_SEARCH_FIELDS: AddressFieldType[] = [
    VENUE_ADDRESS_ADDRESS_1,
    VENUE_ADDRESS_CITY,
    VENUE_ADDRESS_REGION,
    VENUE_ADDRESS_POSTAL,
    VENUE_ADDRESS_COUNTRY,
];

export const escapeStringRegexp = (query: string) => {
    // Escape characters with special meaning either inside or outside character sets.
    return query.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');
};

const filter = (venues: VenueValues[], query?: string) => {
    const sortedByIdVenues = [...venues].sort((a, b) => (a.id > b.id ? 1 : -1));

    if (!query || (query && DISALLOWED_CHARACTERS.includes(query))) {
        const reversedSortedVenues = sortedByIdVenues.reverse();
        return reversedSortedVenues.slice(0, 3);
    }

    const searchRegex = new RegExp(`${escapeStringRegexp(query)}`, 'i');

    const filteredVenues = sortedByIdVenues
        .filter(({ name, address }) => {
            return (
                (name && searchRegex.test(name)) ||
                ADDRESS_SEARCH_FIELDS.some((field) => {
                    const value = address[field];
                    return value && searchRegex.test(value);
                })
            );
        })
        .reverse();

    return filteredVenues.slice(0, 3);
};

export const useVenues = ({
    organizationId,
    blacklistedCountries = new Set<string>(),
}: {
    organizationId: string;
    blacklistedCountries?: Set<string>;
}) => {
    const [query, setQuery] = useState<string>('');
    const { data: venues = [] } = useOrganizationVenues({
        organizationId,
        blacklistedCountries,
    });

    const filteredVenues = useMemo(
        () => filter(venues, query),
        [query, venues],
    );

    return {
        venues: filteredVenues,
        filter: setQuery,
    };
};
